import { RefObject, useLayoutEffect, useState } from 'react';
import { useEventListener, useWindowEventListener } from '@hooks-clean/index';

type Options = {
    trackWidth?: boolean;
    trackHeight?: boolean;
};

/**
 * Tracks width (`offsetWidth`) and height (`offsetHeight`) of an element (by RefObject).
 * Checks on rerender, element resize event, window resize event.
 *
 * Rerenders only if width/height have changed.
 *
 * Options:
 * - `trackWidth` (default true) - whether to track width. If false, width is always undefined.
 * - `trackHeight` (default true) - whether to track height. If false, height is always undefined.
 */
export function useElementSize<TElement extends HTMLElement>(ref: RefObject<TElement | null>, options?: Options) {
    const trackWidth = options?.trackWidth ?? true;
    const trackHeight = options?.trackHeight ?? true;

    const [width, setWidth] = useState<number>();
    const [height, setHeight] = useState<number>();

    function updateSize() {
        // These will only trigger a rerender if they're different to the current width/height
        // Always use undefined if not tracking a dimension
        setWidth(trackWidth ? ref.current?.offsetWidth : undefined);
        setHeight(trackHeight ? ref.current?.offsetHeight : undefined);
    }

    // Refresh the size of the element on rerender, resize, and window resize
    useLayoutEffect(() => updateSize());
    useEventListener(ref, 'resize', () => updateSize());
    useWindowEventListener('resize', () => updateSize());

    return { width, height };
}
